import { Link } from '@tanstack/react-router';
import {
  CornerUpRight,
  Forward,
  Mail,
  MoreVertical,
  Reply,
  ReplyAll,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useComposerState } from './composer-state';
import { EmailTextContent } from './email-text-content';
import {
  ClampedContent,
  MessageBubble,
  MessageBubbleFooter,
  MessageBubbleSubject,
  MessageRow,
  ON_SURFACE_CONTROL,
  ON_SURFACE_LINK,
} from './message-bubble';
import type { MailboxEmailActivity } from './types';
import type { ComposeMode } from '../../mail/-components/types';
import { DeliveryStatus } from '~/components/delivery-status';
import { EmailAttachments } from '~/components/email-attachments';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { formatDateTime } from '~/lib/dates';
import { cn } from '~/lib/utils';

/** No sender or recipient recorded on a forward. */
const UNKNOWN_PARTY = '—';

export function MailboxEmailActivityMessage({
  activity,
}: {
  activity: MailboxEmailActivity;
}) {
  const { t } = useTranslation('tasks');
  const { state, setState } = useComposerState();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const isOutbound = activity.direction === 'outbound';
  const isReplyTarget =
    state.view === 'mail' &&
    (state.mode === 'reply' || state.mode === 'reply-all') &&
    state.target.emailId === activity.emailId;
  // A forward with no note has no text of its own; the subject says what it is.
  const content = activity.body ? (
    <EmailTextContent text={activity.body} />
  ) : (
    activity.forwarded && (
      <span className="text-sm italic text-muted-foreground">
        {t('Forwarded message')}
      </span>
    )
  );

  return (
    <MessageRow isOutbound={isOutbound} handledBy={activity.handledBy}>
      <MessageBubble
        isOutbound={isOutbound}
        isHighlighted={isReplyTarget}
        actions={
          <EmailActions
            activity={activity}
            onCompose={(mode) =>
              setState({
                view: 'mail',
                mode,
                target: {
                  emailId: activity.emailId,
                  subject: activity.subject,
                  fromEmail: activity.fromEmail,
                },
              })
            }
          />
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {activity.subject && (
              <MessageBubbleSubject subject={activity.subject} />
            )}
            <ClampedContent
              isExpanded={isExpanded}
              isOverflowing={isOverflowing}
              onOverflowChange={setIsOverflowing}
              className="min-h-6"
            >
              {content}
            </ClampedContent>
          </div>
          <EmailAttachments
            attachments={activity.attachments}
            onPrimary={isOutbound}
          />
        </div>
        <MessageBubbleFooter
          icon={Mail}
          label={formatDateTime(activity.createdAt)}
          status={<DeliveryStatus badge={activity.deliveryBadge} />}
          leading={
            <span className="flex min-w-0 items-center gap-2">
              {(isOverflowing || isExpanded) && (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className={ON_SURFACE_LINK}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? t('Show less') : t('Show more')}
                </Button>
              )}
              {isReplyTarget && (
                <Badge tone="primary">{t('Replying to')}</Badge>
              )}
            </span>
          }
        />
        <EmailForwards activity={activity} isOutbound={isOutbound} />
      </MessageBubble>
    </MessageRow>
  );
}

function EmailActions({
  activity,
  onCompose,
}: {
  activity: MailboxEmailActivity;
  onCompose: (mode: Exclude<ComposeMode, 'new'>) => void;
}) {
  const { t } = useTranslation('tasks');

  if (activity.folder === null) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={t('More actions')}
          className={ON_SURFACE_CONTROL}
        >
          <MoreVertical strokeWidth={2.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link
            to="/mail/$folder/$threadId"
            params={{ folder: activity.folder, threadId: activity.threadId }}
            className="flex w-full items-center gap-2"
          >
            <Mail className="size-4" strokeWidth={2.5} />
            {t('Open in mailbox')}
          </Link>
        </DropdownMenuItem>
        {activity.actions.reply && (
          <DropdownMenuItem onSelect={() => onCompose('reply')}>
            <Reply className="size-4" strokeWidth={2.5} />
            {t('Reply')}
          </DropdownMenuItem>
        )}
        {activity.actions.replyAll && (
          <DropdownMenuItem onSelect={() => onCompose('reply-all')}>
            <ReplyAll className="size-4" strokeWidth={2.5} />
            {t('Reply all')}
          </DropdownMenuItem>
        )}
        {activity.actions.forward && (
          <DropdownMenuItem onSelect={() => onCompose('forward')}>
            <Forward className="size-4" strokeWidth={2.5} />
            {t('Forward')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmailForwards({
  activity,
  isOutbound,
}: {
  activity: MailboxEmailActivity;
  isOutbound: boolean;
}) {
  const { t } = useTranslation('tasks');
  const forwards = activity.forwards.flatMap((forward) =>
    forward.folder === null ? [] : [{ ...forward, folder: forward.folder }],
  );

  if (forwards.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 border-t pt-2',
        isOutbound ? 'border-white/10' : 'border-border',
      )}
    >
      {forwards.map((forward) => (
        <Link
          key={forward.emailId}
          to="/mail/$folder/$threadId"
          params={{ folder: forward.folder, threadId: forward.threadId }}
          className="flex items-center gap-1.5 text-2xs opacity-80 transition-opacity hover:opacity-100"
        >
          <CornerUpRight className="size-3 shrink-0" strokeWidth={2.5} />
          <span className="min-w-0 flex-1 truncate">
            {t('FWD: {{sender}} to {{recipient}}', {
              recipient: forward.to ?? UNKNOWN_PARTY,
              sender: forward.sentByName ?? UNKNOWN_PARTY,
            })}
          </span>
          <span className="shrink-0">{formatDateTime(forward.createdAt)}</span>
        </Link>
      ))}
    </div>
  );
}
